import React, { Fragment, useContext, useEffect, useMemo } from 'react';
import proptypes from 'prop-types';
import classNames from 'classnames';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import {
  DragAndDrop,
  DroppableContainer,
  generateGroupItems,
} from '@thd-olt-component-react/drag-and-drop';
import {
  Accordion,
  AccordionBody,
  AccordionHeader,
  Checkbox,
} from '@one-thd/sui-atomic-components';
import { GroupAccordionContext } from '../GroupAccordionContext';
import { GroupItem } from '../group-item/GroupItem';
import { GroupHeading } from '../group-details/GroupHeading';
import { GroupContent } from './GroupContent';

const { func, bool, number, shape, string } = proptypes;

const GroupWrapper = ({
  renderPod, onDragEnd, renderGroupButtons, groupToBeAutoOpened, renderDeleteButton,
  showNumbering, placeHolderConfig, onGroupExpand, renderSubtitle, isLoading, renderProductLoader,
  userSelect, noBorders, disablePlaceholders
}) => {
  const {
    data,
    expandedItems,
    setExpandedItems,
    selectedItems,
    disableSelection,
    onSelectGroup,
    onUnselectGroup
  } = useContext(GroupAccordionContext);
  const [groups, setGroups] = React.useState(generateGroupItems(data));
  const [groupChangeStatus, setGroupChangeStatus] = React.useState([]);
  const { channel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';

  // DnD doesn't support to pass context to group items, so we need to map the items using group and item
  // index from "data".
  // NOTE: Allowing to pass context to group items would be a better solution but that has to be done in DnD library.
  const uniqueItemMap = useMemo(() => {
    let index = 0;
    // Injects numbering and uniqueId to each item
    // Uses "data" instead of group because DnD updates groups every time a "drag over" happens.
    const mappedGroups = Object.values(generateGroupItems(data)).map((items, groupIndex) => {
      return {
        ...items,
        items: items.map((item, itemIndex) => {
          index += 1;
          return {
            ...data[groupIndex].items[itemIndex],
            // TODO: This numbering map doen't support infinite scrolling.
            // This numbering is only used for now by ProAssist
            numbering: index,
            id: item
          };
        })
      };
    });

    // converts array into a hashmap using item id as key
    return mappedGroups.reduce((map, group) => {
      return {
        ...map,
        ...group.items.reduce((groupMap, item) => {
          return {
            ...groupMap,
            [item.id]: item
          };
        }, {})
      };
    }, {});
  }, [data]);

  useEffect(() => {
    const gpData = generateGroupItems(data);
    const prevGpData = { ...groups };
    const prevGpKeys = Object.keys(prevGpData);
    let positionChanged = false;
    const isChanged = Object.keys(gpData).map((key, keyIndex) => {
      if (positionChanged) return true;
      if (keyIndex !== prevGpKeys.findIndex((prevKey) => prevKey === key)) {
        positionChanged = true;
        return true;
      }
      return gpData[key].length !== prevGpData?.[key]?.length;
    });
    setGroups(gpData);
    setGroupChangeStatus(isChanged);
  }, [data]);

  const onRearrangeUpdate = ({
    groupedItems, prevGroupId, newGroupId, prevIndex, newIndex, item
  }) => {
    onDragEnd({
      groupedItems, prevGroupId, newGroupId, prevIndex, newIndex, item
    });
  };

  const handleExpandItem = (item) => (event, expanded) => {
    setExpandedItems((currentExpanded) => {
      if (expanded) return [...currentExpanded, item];

      return currentExpanded.filter((_item) => _item !== item);
    });
    onGroupExpand(expanded, item);
  };

  const handleAutoExpandGroup = (groupId) => {
    const expanded = isItemExpanded(groupId);
    if (expanded) return;
    setExpandedItems((currentExpanded) => {
      return [...currentExpanded, groupToBeAutoOpened];
    });
  };

  useEffect(() => {
    if (groupToBeAutoOpened) {
      handleAutoExpandGroup(groupToBeAutoOpened);
    }
  }, [groupToBeAutoOpened]);

  const isItemExpanded = (item) => {
    return expandedItems.includes(item);
  };

  const onCheckChange = (groupId) => {
    if (selectedItems[groupId]?.isSelected && !selectedItems[groupId]?.isIndeterminate) {
      onUnselectGroup(groupId);
    } else {
      onSelectGroup(groupId);
    }
  };

  const accordionContentStyle = classNames(
    'sui-flex sui-items-center'
  );

  const renderGroupContent = ({
    groupItems, group, loadMore, groupIndex, groupChangeStatus: changeStatus
  }) => {
    return (
      <GroupContent
        groupItems={groupItems}
        group={group}
        loadMoreGroupItems={loadMore}
        placeHolderConfig={placeHolderConfig}
        uniqueItemMap={uniqueItemMap}
        renderPod={renderPod}
        showNumbering={showNumbering}
        groupIndex={groupIndex}
        groupChangeStatus={changeStatus}
        userSelect={userSelect}
        renderDeleteButton={renderDeleteButton}
        disablePlaceholders={disablePlaceholders}
      />
    );
  };

  return (
    <DragAndDrop
      id="group-wrapper"
      groups={groups}
      setGroups={setGroups}
      onRearrangeUpdate={onRearrangeUpdate}
      renderDraggedItem={(dragObj) => {
        const item = uniqueItemMap[dragObj.id];
        return (
          <GroupItem
            id={dragObj.id}
            item={dragObj.item}
            groupId={dragObj.groupId}
            renderPod={renderPod}
            numbering={showNumbering ? item?.numbering : undefined}
            alert={item?.alert}
            renderDeleteButton={renderDeleteButton}
          />
        );
      }}
    >
      {
        data.map((group, groupIndex) => {
          const {
            id: groupId, noOfItems = group?.items?.length, isDefault
          } = group;
          const groupItems = groups[groupId];
          // When bringing back a recently removed group, groupItems loses the info of the group on the first re-render
          // because it first re-renders when new data context is passed, and then triggers the useEffect
          // where groups are set again.
          // An example of this case would be optimistic updates, where the client first removes the data, then executes
          // a mutation, and then if the mutation fails, fallbacks to the previous state.
          if (!groupItems) return <Fragment key={`empty-group-${groupId}`} />;
          const GroupButtons = renderGroupButtons ? renderGroupButtons(group) : null;

          const expanded = isItemExpanded(groupId);

          const GroupWrapperStyle = classNames(
            'sui-flex sui-w-full',
            { 'sui-pb-3': expanded }
          );
          const defaultGroupWrapperStyle = classNames(
            'sui-flex sui-flex-col sui-w-full sui-pb-4',
            {
              'sui-px-2': isMobile
            }
          );
          return (
            <Fragment key={`group-wrapper-fragment-${groupId}`}>
              {
                isDefault && (
                  <DroppableContainer id={groupId} items={groupItems} isClosed={Boolean(groupItems?.length)}>
                    {
                      noOfItems === 0 && renderSubtitle && renderSubtitle({ groupId })
                    }
                    {
                      noOfItems > 0 && (
                        <div
                          className={defaultGroupWrapperStyle}
                          data-testid={`group-wrapper-${groupId}`}
                          key={`group-wrapper-${groupId}`}
                        >
                          { renderSubtitle && renderSubtitle({ groupId }) }
                          {
                            renderProductLoader
                              ? renderProductLoader({
                                groupItems, groupId, renderGroupContent, groupIndex, groupChangeStatus
                              })
                              : renderGroupContent({
                                groupItems, group, loadMore: null, groupIndex, groupChangeStatus
                              })
                          }
                        </div>
                      )
                    }
                  </DroppableContainer>
                )
              }
              {
                !isDefault && (
                  <div
                    className={GroupWrapperStyle}
                    data-testid={`group-wrapper-${groupId}`}
                    key={`group-wrapper-${groupId}`}
                  >
                    <Accordion
                      data-component="GroupAccordion"
                      style={{ width: '100%' }}
                      onChange={handleExpandItem(groupId)}
                      expanded={expanded}
                      noBorders={noBorders}
                    >
                      {
                        expanded && GroupButtons && (
                          <div
                            data-testid={`group-buttons-${groupId}`}
                            className="sui-flex sui-justify-end sui-pb-3"
                          >
                            {GroupButtons}
                          </div>
                        )
                      }
                      <div className={accordionContentStyle}>
                        {
                          !disableSelection && (
                            <Checkbox
                              checked={Boolean(selectedItems[groupId]?.isSelected)}
                              indeterminate={Boolean(
                                selectedItems[groupId]?.isIndeterminate,
                              )}
                              onChange={() => onCheckChange(groupId)}
                              data-id="list-group-check-box"
                            />
                          )
                        }
                        {
                          !expanded && (
                            <div className="sui-w-full">
                              <DroppableContainer
                                id={groupId}
                                items={groupItems}
                                key={`header-${groupId}`}
                                closedContainer
                              >
                                <AccordionHeader
                                  data-testid={`group-header-${groupId}`}
                                  aria-controls={`group-content-${groupId}`}
                                  id={`group-header-${groupId}`}
                                  style={{
                                    width: '100%',
                                  }}
                                >
                                  <GroupHeading group={group} renderSubtitle={renderSubtitle} />
                                </AccordionHeader>
                              </DroppableContainer>
                            </div>
                          )
                        }
                        {
                          expanded && (
                            <AccordionHeader
                              data-testid={`group-header-${groupId}`}
                              aria-controls={`group-content-${groupId}`}
                              id={`group-header-${groupId}`}
                              style={{
                                width: '100%',
                              }}
                            >
                              <GroupHeading group={group} renderSubtitle={renderSubtitle} />
                            </AccordionHeader>
                          )
                        }
                      </div>
                      <AccordionBody
                        data-testid={`group-body-${groupId}`}
                        style={{
                          // Checkbox has a fixed width of 36px in and desktop
                          marginLeft: (disableSelection || isMobile) ? '0px' : '36px',
                        }}
                      >
                        {
                          expanded && (
                            <DroppableContainer id={groupId} items={groupItems} key={groupId}>
                              <div className="sui-min-h-16">
                                {
                                  renderProductLoader
                                    ? renderProductLoader({
                                      groupItems, groupId, renderGroupContent, groupIndex, groupChangeStatus
                                    })
                                    : renderGroupContent({
                                      groupItems, group, loadMore: null, groupIndex, groupChangeStatus
                                    })
                                }
                              </div>
                            </DroppableContainer>
                          )
                        }
                      </AccordionBody>
                    </Accordion>
                  </div>
                )
              }
            </Fragment>
          );
        })
      }
    </DragAndDrop>
  );
};

GroupWrapper.displayName = 'GroupWrapper';

GroupWrapper.propTypes = {
  renderPod: func.isRequired,
  placeHolderConfig: shape({
    height: number,
    numberOfLines: number,
    numberOfPlaceHolders: number
  }).isRequired,
  isLoading: bool.isRequired,
  renderProductLoader: func,
  onDragEnd: func,
  renderSubtitle: func,
  renderGroupButtons: func,
  onGroupExpand: func,
  showNumbering: bool,
  groupToBeAutoOpened: string,
  userSelect: string,
  renderDeleteButton: func,
  disablePlaceholders: bool
};

GroupWrapper.defaultProps = {
  renderGroupButtons: null,
  onGroupExpand: () => {},
  onDragEnd: () => {},
  showNumbering: false,
  renderSubtitle: null,
  renderProductLoader: null,
  groupToBeAutoOpened: '',
  userSelect: 'none',
  renderDeleteButton: () => {},
  disablePlaceholders: false
};

export { GroupWrapper };
